import { iamApiGet, iamApiPost } from '@/lib/api/iam-api-client';
import type {
  GetOrganizationsResponse,
  ListOrganizationsResponse,
} from '@/lib/api/types/multi-orgs.types';
import { clients } from '@/lib/https';
import { ChangePasswordPayload } from '../types/account.type';
import { User } from '@/types/user.type';
import { CreateUserFormType, ProfileFormType } from '../components/utils/index.utils';

export const changePassword = async (payload: ChangePasswordPayload) => {
  payload.projectKey = payload.projectKey ?? import.meta.env.VITE_X_BLOCKS_KEY;
  return clients.post('/idp/v1/Iam/ChangePassword', JSON.stringify(payload));
};

export const getAccount = async (): Promise<User> => {
  const res = await clients.get<{ data: User }>('/iam/v4/iam/me');
  return res.data;
};

export const getMyOrganizations = async (): Promise<GetOrganizationsResponse> => {
  return iamApiGet<GetOrganizationsResponse>('/organizations/my');
};

export const listOrganizations = async (
  params: { page?: number; pageSize?: number } = {}
): Promise<GetOrganizationsResponse> => {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const query = new URLSearchParams({
    Page: String(page),
    PageSize: String(pageSize),
  });

  const response = await iamApiGet<ListOrganizationsResponse>(`/organizations?${query.toString()}`);

  const nested = response.data;
  const fromNested = Array.isArray(nested)
    ? nested
    : (nested?.organizations ?? nested?.items ?? []);
  const organizations =
    response.organizations ??
    response.items ??
    fromNested ??
    [];

  return {
    ...response,
    organizations: Array.isArray(organizations) ? organizations : [],
    isSuccess: response.isSuccess ?? true,
  };
};

/**
 * Assign the user to an existing organization (membership grant — does not create an org).
 * POST /iam/v4/iam/users/access
 */
export const assignUserToOrganization = async (params: {
  userId: string;
  organizationId: string;
  roles?: string[];
}): Promise<unknown> => {
  const userId = params.userId.trim();
  const organizationId = params.organizationId.trim();
  if (!userId || !organizationId) {
    throw new Error('User id and organization id are required');
  }

  return iamApiPost('/users/access', {
    userId,
    organizationId,
    roles: params.roles?.length ? params.roles : ['user'],
  });
};

export const updateAccount = (data: ProfileFormType) => {
  return clients.post<User>('/iam/v4/iam/me', JSON.stringify(data));
};

export const createAccount = (data: CreateUserFormType) => {
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>('/iam/v4/iam/users/create', JSON.stringify(data));
};
